import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, PasswordInput } from '../components/ui';
import { safeErrorMessage } from '../utils/apiError';

function slugify(value) {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function TenantProvision({ token }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '', slug: '',
    principal_full_name: '', principal_username: '', principal_email: '', principal_password: '',
    administrator_full_name: '', administrator_username: '', administrator_email: '', administrator_password: '',
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData((prev) => ({ ...prev, name, slug: slugTouched ? prev.slug : slugify(name) }));
  };

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/v1/superadmin/schools', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `School "${data.school.name}" provisioned successfully.` });
        setTimeout(() => navigate(`/superadmin/schools/${data.school.id}`), 1200);
      } else {
        setMessage({ type: 'error', text: await safeErrorMessage(response, 'Failed to provision school') });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Provision New School</h2>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
          Creates the school, its first principal and administrator accounts, and default terms/subjects.
        </p>
      </div>

      {message && <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>{message.text}</div>}

      <Card>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>School Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleNameChange} required placeholder="e.g. Greenfield High School" />
          </div>
          <div className="form-group">
            <label>School Code (slug)</label>
            <input
              type="text"
              name="slug"
              value={formData.slug}
              onChange={(e) => { setSlugTouched(true); handleChange(e); }}
              required
              placeholder="e.g. greenfield"
            />
            <small style={{ color: 'var(--color-text-muted)' }}>This is what staff type to sign in — must be unique.</small>
          </div>

          <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '1rem' }}>First Principal Account</h3>

          <div className="form-group">
            <label>Full Name</label>
            <input type="text" name="principal_full_name" value={formData.principal_full_name} onChange={handleChange} required placeholder="e.g. Jane Moyo" />
          </div>
          <div className="form-group">
            <label>Username</label>
            <input type="text" name="principal_username" value={formData.principal_username} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="principal_email" value={formData.principal_email} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <PasswordInput name="principal_password" value={formData.principal_password} onChange={handleChange} required minLength={6} />
          </div>

          <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '1rem' }}>Administrator Account</h3>
          <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '-0.5rem', marginBottom: '1rem' }}>
            Full-access account for this school. Only Super Admin can create or add administrator accounts.
          </p>

          <div className="form-group">
            <label>Full Name</label>
            <input type="text" name="administrator_full_name" value={formData.administrator_full_name} onChange={handleChange} required placeholder="e.g. Tafadzwa Sandulani" />
          </div>
          <div className="form-group">
            <label>Username</label>
            <input type="text" name="administrator_username" value={formData.administrator_username} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="administrator_email" value={formData.administrator_email} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Password</label>
            <PasswordInput name="administrator_password" value={formData.administrator_password} onChange={handleChange} required minLength={6} />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Provisioning...' : 'Provision School'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/superadmin/schools')}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default TenantProvision;
